/**
 * Visit and Job details in the side sheet (job page v2, slice 4). These used
 * to live in the "Job details" drawer; now they sit between Contact and the
 * conversation and open right there.
 *
 * - **Visit** is the rail's Schedule & Crew card as-is (date, tech, crew and
 *   helpers, confirm booking, priority, another visit, cannot-complete, visit
 *   history), flattened into the sheet. "Change date or tech" (the header's
 *   tech and date, a pin, ⌘K) scrolls here with its editor open.
 * - **Job details** is one line (type · due · PO · WO) that opens to the
 *   rail's Details card, the WO mapping card, the ask and "more about this
 *   job" (description, replacements, legacy devices, warranties).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { formatDate } from "@/lib/utils";
import { customerWoNumber } from "@/lib/wo-number";
import type { JobDetail } from "../types";
import type { JobDetailsMode } from "./calm-body";
import { Quiet } from "./calm-ui";

const FLAT =
    "space-y-3 [&>*]:rounded-none [&>*]:border-0 [&>*]:bg-transparent [&>*]:shadow-none [&_.rounded-xl.border]:border-0 [&_.rounded-xl.border]:shadow-none";

export function VisitBox({
    job,
    ask,
    children,
}: {
    job: JobDetail;
    ask?: { mode: JobDetailsMode; n: number } | null;
    children: ReactNode;
}) {
    const ref = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (ask?.mode === "schedule") ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ask?.n]);
    return (
        <div
            ref={ref}
            id="calm-visit"
            className="scroll-mt-40 border-b px-[14px] pb-2 pt-3"
            data-testid="calm-visit"
            aria-label={`Visit${job.assigned_to_name ? ` · ${job.assigned_to_name}` : ""}`}
            role="region"
        >
            <div className={FLAT}>{children}</div>
        </div>
    );
}

export function jobDetailsLine(job: JobDetail): string {
    const wo = customerWoNumber(job, { includePo: false });
    return [
        job.job_type_name || null,
        job.due_by_date ? `Due ${formatDate(job.due_by_date)}` : null,
        job.customer_po?.trim() ? `PO ${job.customer_po.trim()}` : null,
        wo ? `WO ${wo}` : null,
    ]
        .filter(Boolean)
        .join(" · ");
}

export function JobDetailsBox({
    job,
    ask,
    children,
}: {
    job: JobDetail;
    ask?: { mode: JobDetailsMode; n: number } | null;
    children: ReactNode;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (ask?.mode !== "details") return;
        setOpen(true);
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ask?.n]);
    const line = jobDetailsLine(job);
    return (
        <div
            ref={ref}
            id="calm-details"
            className="scroll-mt-40 border-b px-[22px] pb-3 pt-4"
            data-testid="calm-details"
        >
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 className="m-0 text-[16px] font-bold text-foreground">Job details</h2>
                <Quiet
                    className="ml-auto"
                    onClick={() => setOpen((o) => !o)}
                    aria-expanded={open}
                    aria-controls="calm-details-open"
                    data-testid="calm-details-toggle"
                >
                    {open ? "Close ▴" : "Edit ▸"}
                </Quiet>
            </div>
            {line && <p className="m-0 mt-1 text-[14px] text-foreground/80">{line}</p>}
            {open && (
                <div id="calm-details-open" className={`mt-2 ${FLAT}`} data-testid="calm-details-open">
                    {children}
                </div>
            )}
        </div>
    );
}
